import { useRollbar } from '@rollbar/react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import React, { useMemo } from 'react'
import { Navigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { BOOK_BASE } from '../../utils/constants'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { Clickable } from '../common/Clickable'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { TextField } from '../common/fields/TextField'
import { useLoginContext } from './LoginProvider'

function SmartPhoneIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M17 2H7C5.89543 2 5 2.89543 5 4V20C5 21.1046 5.89543 22 7 22H17C18.1046 22 19 21.1046 19 20V4C19 2.89543 18.1046 2 17 2Z"
                stroke="#314A68"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15 6H12H9"
                stroke="#314A68"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15 9H12H9"
                stroke="#314A68"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15 12H12H9"
                stroke="#314A68"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M12 18H12.01"
                stroke="#314A68"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export const CodeEntry = () => {
    const navigate = usePSNavigate()
    const rollbar = useRollbar()
    const queryClient = useQueryClient()

    const { loginState, setLoginState } = useLoginContext()
    const [searchParams] = useSearchParams()
    const redirectUrl = searchParams.get('redirectUrl')

    const [loginCode, setLoginCode] = React.useState('')

    const [isModalOpen, setIsModalOpen] = React.useState(false)
    const [showRegisterModal, setShowRegisterModal] = React.useState(false)

    const title = useMemo(() => {
        if (loginState.loginType === 'password') {
            return 'Enter your passcode'
        }
        return 'Enter verification code'
    }, [loginState.loginType])

    const sendLoginCode = async (e: React.SyntheticEvent) => {
        e.preventDefault()
        if (loginState.isSending || !loginState.e164) return

        setLoginState((s) => ({ ...s, isSending: true }))

        try {
            const resp = await RecordFactory.getInstance().login(
                { e164: loginState.e164, code: loginCode, redirectUrl },
                queryClient,
                rollbar,
                true
            )
            if (!resp.ok) {
                setLoginState((s) => ({ ...s, isSending: false }))
                setShowRegisterModal(true)
                return
            }

            if (resp.value.requiresRole) {
                navigate('/login/accounts')
                return
            }

            if (redirectUrl) navigate(redirectUrl)
            else if (resp.value.login.role === 'owner' || resp.value.login.role === 'admin')
                navigate('/')
            else if (resp.value.login.plan === 'franchise') navigate('/reports')
            else navigate('/clients')
        }
        catch (error: any) {
            setLoginState((s) => ({ ...s, isSending: false }))
            const data = error as { title: string; message: string } | undefined
            if (data?.title && data?.message) {
                alert(`${data.title}\n\n${data.message}`)
            }
            else if (data?.message) {
                alert(`${data.message}`)
            }
            else {
                rollbar.error('A login error has occurred', {
                    error: error.toString ? error.toString() : error,
                })
                console.error('A login error has occurred', error)
                alert('An error has occurred')
            }
        }
    }

    if (!loginState.e164) {
        return <Navigate to={`/login${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''}`} />
    }

    return (
        <>
            <form onSubmit={sendLoginCode}>
                <span className="flex flex-col gap-y-3">
                    <Label>{title}</Label>
                    <TextField
                        type="password"
                        name="phoneNumber"
                        size="medium"
                        placeholder={title}
                        isFullWidth
                        autoFocus
                        onChange={(e) => {
                            setLoginCode(e.target.value)
                        }}
                        value={loginCode}
                    />
                </span>
                <Button
                    intent="primary"
                    type="submit"
                    className="mt-6 w-full"
                    disabled={!loginCode}
                    isLoading={loginState.isSending}
                >
                    Sign in
                </Button>

                {/* TODO add this back in when functionality is ready */}
                {/* <div className="flex w-full justify-center">
                    <Button
                        intent="tertiaryLink"
                        size="small"
                        onClick={() => setIsModalOpen(true)}
                        type="button"
                    >
                        Didn't receive a text?
                    </Button>
                </div> */}
            </form>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Didn't receive a text?"
                size="large"
            >
                <div className="flex flex-col gap-y-3">
                    <Clickable
                        onClick={() => {
                            setIsModalOpen(false)
                        }}
                        className={clsx(
                            'flex items-center justify-between rounded-lg border border-gray-300 p-4'
                        )}
                    >
                        <div className="flex items-center gap-x-4">
                            <Icon icon="phone.medium" background="round" className="size-12" />
                            <Label>Get the sign in code by receiving a phone call</Label>
                        </div>
                        <Icon icon="chevronRight.large" />
                    </Clickable>

                    <Clickable
                        onClick={() => {
                            setIsModalOpen(false)
                        }}
                        className={clsx(
                            'flex items-center justify-between rounded-lg border border-gray-300 p-4'
                        )}
                    >
                        <div className="flex items-center gap-x-4">
                            <span className="bg-primary-100 flex size-12 flex-col items-center justify-center rounded-full">
                                <SmartPhoneIcon />
                            </span>
                            <Label>Auto verify by phone</Label>
                        </div>
                        <Icon icon="chevronRight.large" />
                    </Clickable>
                </div>
            </Modal>

            <ConfirmationModal
                isOpen={showRegisterModal}
                onClose={() => setShowRegisterModal(false)}
                title="Register your business"
                confirmButtonText="OK"
                onConfirm={() => {
                    window.location.href = `${BOOK_BASE}/register`
                }}
                onCancel={() => {
                    setShowRegisterModal(false)
                }}
            >
                <Paragraph>
                    Whoops! Looks like you don't have an account with PocketSuite yet.
                    <br />
                    Please press OK to start registering your business.
                </Paragraph>
            </ConfirmationModal>
        </>
    )
}
